import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, ImageIcon, PartyPopper, Sparkles } from 'lucide-react';
import { clsx } from 'clsx';
import {
  clearActivityScore,
  readWorkbookJson,
  writeActivityScore,
  writeWorkbookJson,
  type WorkbookActivityCompletion,
} from './workbookPersist';

export type ImageLabelItem = {
  id: number;
  image: string;
  answer: string;
  options: string[];
};

export interface ImageLabelCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  sourcePage?: number;
  items: ImageLabelItem[];
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

type ItemCell = { st: 'idle' | 'correct' | 'wrong'; pick?: string };

type PersistShapeV3 = { version: 3; cells: Record<string, ItemCell> };
type PersistShapeV2 = { version: 2; solved: Record<string, boolean> };

function norm(s: string): string {
  let t = String(s).trim().replace(/\s+/g, ' ');
  try {
    t = t.normalize('NFKC');
  } catch {
    /* ignore */
  }
  return t;
}

function isMatch(pick: string, answer: string): boolean {
  return norm(pick) === norm(answer);
}

const cardThemes = [
  {
    shell: 'border-fuchsia-200/90 bg-gradient-to-br from-fuchsia-50 via-rose-50 to-amber-50 shadow-[0_20px_50px_-12px_rgba(232,121,249,0.35)] dark:border-fuchsia-900/50 dark:from-fuchsia-950/35 dark:via-slate-900 dark:to-slate-900',
    head: 'border-fuchsia-200/70 bg-white/60 dark:border-fuchsia-800/40 dark:bg-slate-900/50',
    accent: 'text-fuchsia-900 dark:text-fuchsia-100',
    chipIdle: 'border-fuchsia-200/90 bg-white/90 text-fuchsia-950 hover:border-amber-300 hover:bg-amber-50/80 dark:border-fuchsia-800 dark:bg-slate-800/90 dark:text-fuchsia-50',
  },
  {
    shell: 'border-sky-200/90 bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50 shadow-[0_20px_50px_-12px_rgba(14,165,233,0.35)] dark:border-sky-900/50 dark:from-sky-950/35 dark:via-slate-900 dark:to-slate-900',
    head: 'border-sky-200/70 bg-white/60 dark:border-sky-800/40 dark:bg-slate-900/50',
    accent: 'text-sky-900 dark:text-sky-100',
    chipIdle: 'border-sky-200/90 bg-white/90 text-sky-950 hover:border-amber-300 hover:bg-amber-50/80 dark:border-sky-800 dark:bg-slate-800/90 dark:text-sky-50',
  },
  {
    shell: 'border-violet-200/90 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 shadow-[0_20px_50px_-12px_rgba(139,92,246,0.3)] dark:border-violet-900/50 dark:from-violet-950/35 dark:via-slate-900 dark:to-slate-900',
    head: 'border-violet-200/70 bg-white/60 dark:border-violet-800/40 dark:bg-slate-900/50',
    accent: 'text-violet-900 dark:text-violet-100',
    chipIdle: 'border-violet-200/90 bg-white/90 text-violet-950 hover:border-amber-300 hover:bg-amber-50/80 dark:border-violet-800 dark:bg-slate-800/90 dark:text-violet-50',
  },
];

function emptyCellsForItems(list: ImageLabelItem[]): Record<number, ItemCell> {
  const m: Record<number, ItemCell> = {};
  for (const it of list) m[it.id] = { st: 'idle' };
  return m;
}

export const ImageLabelCard: React.FC<ImageLabelCardProps> = ({
  workbookLabel,
  title,
  instructions,
  sourcePage,
  items,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-imgpick-${persistenceId}` : '';

  const itemsRef = useRef(items);
  itemsRef.current = items;

  /** String key only — do not depend on `items` array identity (parent often passes a new ref each render). */
  const itemsStableKey = items
    .map((it) => `${it.id}\u0001${it.image}\u0001${norm(it.answer)}\u0001${it.options.map(norm).join('\u0002')}`)
    .sort()
    .join('\u0003');

  const [cells, setCells] = useState<Record<number, ItemCell>>(() => emptyCellsForItems(items));
  const [sessionTouched, setSessionTouched] = useState(false);
  const [brokenImages, setBrokenImages] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const list = itemsRef.current;
    const base = emptyCellsForItems(list);

    if (!storageKey) {
      setCells(base);
      setSessionTouched(false);
      setBrokenImages({});
      return;
    }

    const loaded = readWorkbookJson<
      Partial<PersistShapeV3> & Partial<PersistShapeV2> | Record<string, unknown>[] | null
    >(storageKey, null);
    const merged: Record<number, ItemCell> = { ...base };

    if (Array.isArray(loaded)) {
      setCells(merged);
      setSessionTouched(false);
      setBrokenImages({});
      return;
    }

    if (loaded && typeof loaded === 'object' && loaded.version === 3 && loaded.cells && typeof loaded.cells === 'object') {
      const raw = loaded.cells as Record<string, unknown>;
      for (const k of Object.keys(raw)) {
        const id = Number(k);
        if (!Number.isFinite(id) || merged[id] === undefined) continue;
        const row = raw[k] as { st?: unknown; pick?: unknown };
        if (row?.st === 'correct') merged[id] = { st: 'correct' };
        else if (row?.st === 'wrong') merged[id] = { st: 'wrong', pick: typeof row.pick === 'string' ? row.pick : undefined };
      }
    } else if (loaded && typeof loaded === 'object' && loaded.version === 2 && loaded.solved && typeof loaded.solved === 'object') {
      const raw = loaded.solved as Record<string, unknown>;
      for (const k of Object.keys(raw)) {
        const id = Number(k);
        if (!Number.isFinite(id) || merged[id] === undefined) continue;
        if (raw[k] === true) merged[id] = { st: 'correct' };
      }
    }

    setCells(merged);
    setBrokenImages({});
    setSessionTouched(Object.values(merged).some((c) => c.st !== 'idle'));
  }, [storageKey, itemsStableKey, items.length]);

  useEffect(() => {
    if (!storageKey) return;
    const cellsOut: Record<string, ItemCell> = {};
    for (const it of items) {
      const c = cells[it.id] ?? { st: 'idle' };
      if (c.st === 'idle') continue;
      cellsOut[String(it.id)] = c.st === 'wrong' && c.pick ? { st: 'wrong', pick: c.pick } : { st: c.st };
    }
    writeWorkbookJson(storageKey, { version: 3, cells: cellsOut } satisfies PersistShapeV3);
  }, [storageKey, cells, items]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  const getCell = useCallback((id: number) => cells[id] ?? { st: 'idle' as const }, [cells]);

  const correctCount = useMemo(
    () => items.filter((it) => getCell(it.id).st === 'correct').length,
    [items, getCell]
  );
  const allCorrect = useMemo(
    () => items.length > 0 && items.every((it) => getCell(it.id).st === 'correct'),
    [items, getCell]
  );
  const anySettled = useMemo(
    () => items.some((it) => getCell(it.id).st !== 'idle'),
    [items, getCell]
  );
  const allSettled = useMemo(
    () => items.length > 0 && items.every((it) => getCell(it.id).st !== 'idle'),
    [items, getCell]
  );

  useEffect(() => {
    if (!onWorkbookStatusChange) return;
    if (items.length === 0) {
      onWorkbookStatusChange('completed');
      return;
    }
    if (allCorrect) {
      onWorkbookStatusChange('completed');
      return;
    }
    if (anySettled || sessionTouched) {
      onWorkbookStatusChange('in_progress');
      return;
    }
    onWorkbookStatusChange('not_started');
  }, [onWorkbookStatusChange, items.length, allCorrect, anySettled, sessionTouched]);

  useEffect(() => {
    if (!persistenceId || items.length === 0 || !allSettled) return;
    writeActivityScore(persistenceId, (correctCount / items.length) * 100);
  }, [persistenceId, items.length, allSettled, correctCount]);

  const onPick = (item: ImageLabelItem, option: string) => {
    const cur = cells[item.id] ?? { st: 'idle' };
    if (cur.st !== 'idle') return;

    mark();
    setSessionTouched(true);

    if (isMatch(option, item.answer)) {
      setCells((prev) => ({ ...prev, [item.id]: { st: 'correct' } }));
      return;
    }

    setCells((prev) => ({ ...prev, [item.id]: { st: 'wrong', pick: option } }));
  };

  const resetAll = () => {
    mark();
    setSessionTouched(false);
    setBrokenImages({});
    const next = emptyCellsForItems(items);
    setCells(next);
    if (storageKey) {
      writeWorkbookJson(storageKey, { version: 3, cells: {} } satisfies PersistShapeV3);
    }
    if (persistenceId) clearActivityScore(persistenceId);
    onWorkbookStatusChange?.('not_started');
  };

  return (
    <div className="overflow-hidden rounded-[2rem] border-4 border-amber-200/80 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 shadow-[0_24px_60px_-14px_rgba(251,191,36,0.35)] dark:border-amber-900/40 dark:from-amber-950/30 dark:via-slate-900 dark:to-slate-900">
      <div className="border-b border-amber-200/70 bg-white/55 px-5 py-5 backdrop-blur-sm dark:border-amber-800/40 dark:bg-slate-900/50">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg">
            <Sparkles className="h-8 w-8" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            {workbookLabel ? (
              <p className="text-sm font-extrabold text-amber-900 tamil-text dark:text-amber-200" lang="ta">
                {workbookLabel}
              </p>
            ) : null}
            <h2 className="mt-1 text-2xl font-extrabold text-amber-950 tamil-text md:text-3xl dark:text-amber-50" lang="ta">
              {title}
            </h2>
            {instructions ? (
              <p className="mt-2 text-lg leading-relaxed text-amber-950/95 tamil-text dark:text-amber-100/90" lang="ta">
                {instructions}
              </p>
            ) : null}
            {sourcePage != null ? (
              <span className="mt-3 inline-block rounded-full bg-amber-600 px-3 py-1 text-sm font-bold text-white">p.{sourcePage}</span>
            ) : null}
          </div>
        </div>
        {allCorrect ? (
          <p
            className="mt-4 flex items-center gap-2 rounded-2xl border-2 border-emerald-300/80 bg-emerald-50/90 px-4 py-3 text-base font-bold text-emerald-900 tamil-text dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
            lang="ta"
          >
            <PartyPopper className="h-6 w-6 shrink-0 text-emerald-600 dark:text-emerald-300" aria-hidden />
            அருமை! எல்லாப் படங்களும் சரியாகத் தேர்ந்தெடுக்கப்பட்டன.
          </p>
        ) : null}
      </div>

      <div className="space-y-10 px-4 py-8 sm:px-8">
        {items.length === 0 ? (
          <p className="text-center text-lg font-semibold text-amber-900 tamil-text dark:text-amber-100" lang="ta">
            இந்தச் செயலுக்குப் பட உருப்படிகள் இன்னும் சேர்க்கப்படவில்லை.
          </p>
        ) : (
          items.map((it, idx) => {
            const theme = cardThemes[idx % cardThemes.length];
            const cell = getCell(it.id);
            const done = cell.st === 'correct';
            const failed = cell.st === 'wrong';
            const wrongPick = failed ? cell.pick : undefined;

            return (
              <article
                key={it.id}
                className={clsx(
                  'overflow-hidden rounded-[1.75rem] border-4 p-4 sm:p-6',
                  theme.shell,
                  done && 'ring-4 ring-emerald-300/90 ring-offset-2 ring-offset-amber-50 dark:ring-emerald-600/80 dark:ring-offset-slate-900',
                  failed && 'ring-4 ring-rose-300/90 ring-offset-2 ring-offset-amber-50 dark:ring-rose-700/80 dark:ring-offset-slate-900'
                )}
              >
                <div
                  className={clsx(
                    'mb-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl border-2 px-4 py-3 backdrop-blur-sm',
                    theme.head
                  )}
                >
                  <span className={clsx('text-lg font-black tamil-text', theme.accent)} lang="ta">
                    படம் {idx + 1}
                  </span>
                  {done ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-sm font-black text-white shadow-md">
                      <CheckCircle2 className="h-5 w-5" aria-hidden />
                      சரி!
                    </span>
                  ) : failed ? (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 px-3 py-1.5 text-sm font-black text-white shadow-md tamil-text"
                      lang="ta"
                    >
                      தவறு
                    </span>
                  ) : null}
                </div>

                <div className="overflow-hidden rounded-3xl border-4 border-white bg-white/80 shadow-inner dark:border-slate-600 dark:bg-slate-800/80">
                  <div className="relative aspect-[4/3] w-full bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
                    {it.image?.trim() && !brokenImages[it.id] ? (
                      <img
                        src={it.image}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                        onError={() => setBrokenImages((p) => ({ ...p, [it.id]: true }))}
                      />
                    ) : (
                      <div className="flex h-full min-h-[12rem] flex-col items-center justify-center gap-2 p-6 text-center">
                        <ImageIcon className="h-14 w-14 text-slate-400 dark:text-slate-500" aria-hidden />
                        <p className="text-sm font-bold text-slate-600 tamil-text dark:text-slate-300" lang="ta">
                          படம் இணைக்கப்படும்
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <p className="mt-4 text-center text-base font-bold text-slate-700 tamil-text dark:text-slate-200" lang="ta">
                  ஒரு படத்துக்கு ஒரு தேர்வு மட்டும். தவறானால் மீண்டும் முயல் மூலம் செயலை மீட்டமைக்கவும்.
                </p>

                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {it.options.map((opt, optIdx) => {
                    const correct = isMatch(opt, it.answer);
                    const isWrongPick =
                      failed && wrongPick != null && isMatch(opt, wrongPick) && !correct;
                    const chipBase =
                      'min-h-[3.5rem] rounded-2xl border-4 px-4 py-4 text-center text-lg font-extrabold leading-snug transition tamil-text shadow-sm sm:min-h-[4rem] sm:text-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-300/90';

                    let chipClass: string;
                    if (done && correct) {
                      chipClass = clsx(
                        chipBase,
                        'cursor-default border-emerald-500 bg-emerald-100 text-emerald-950 shadow-[0_0_0_3px_rgba(16,185,129,0.35)] dark:border-emerald-400 dark:bg-emerald-900/50 dark:text-emerald-50'
                      );
                    } else if ((done || failed) && !correct) {
                      chipClass = clsx(chipBase, theme.chipIdle, 'cursor-not-allowed opacity-40 grayscale');
                    } else if (isWrongPick) {
                      chipClass = clsx(
                        chipBase,
                        'cursor-default border-rose-500 bg-rose-100 text-rose-950 ring-2 ring-rose-400/80 dark:border-rose-400 dark:bg-rose-900/55 dark:text-rose-50 dark:ring-rose-500/50'
                      );
                    } else {
                      chipClass = clsx(chipBase, theme.chipIdle);
                    }

                    return (
                      <button
                        key={`${it.id}-opt-${optIdx}`}
                        type="button"
                        disabled={done || failed}
                        onClick={() => onPick(it, opt)}
                        className={chipClass}
                        lang="ta"
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </article>
            );
          })
        )}

        {items.length > 0 ? (
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={resetAll}
              className="min-h-[52px] rounded-2xl border-4 border-amber-600 bg-white px-8 py-3 text-lg font-extrabold text-amber-900 shadow-md transition hover:bg-amber-50 dark:bg-slate-800 dark:text-amber-100 dark:hover:bg-slate-700 tamil-text"
              lang="ta"
            >
              மீண்டும் முயல்
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
